import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';

/**
 * A protective wrapper for admin routes with enhanced validation and debugging.
 * - Checks for the existence of a valid admin authentication token in localStorage.
 * - Validates token structure to ensure it's an admin token (not a regular user token).
 * - Provides detailed debug logging in development mode.
 * - If token is invalid/missing, redirects to admin login page.
 * 
 * @param {React.Component} children - The admin page component to be rendered if authenticated.
 */
const ProtectedAdminRoute = ({ children }) => {
  const [isValidating, setIsValidating] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    /**
     * Validate the admin authentication token
     */
    const validateAdminToken = () => {
      // Get tokens from storage
      const adminToken = localStorage.getItem('adminToken');
      const regularToken = localStorage.getItem('token');
      
      // Debug logging (only in development)
      if (process.env.NODE_ENV === 'development') {
        console.log('🔐 ProtectedAdminRoute Debug:');
        console.log('   - adminToken exists:', !!adminToken);
        console.log('   - regularToken exists:', !!regularToken);
        console.log('   - adminToken value:', adminToken ? `${adminToken.substring(0, 20)}...` : 'null');
        
        if (adminToken && regularToken) {
          console.warn('   ⚠️  Both admin and regular user tokens found. Potential conflict.');
        }
      }

      // Check if admin token exists
      if (!adminToken) {
        if (process.env.NODE_ENV === 'development') {
          console.log('   ❌ Redirecting: No adminToken found in localStorage');
        }
        setIsAuthenticated(false);
        setIsValidating(false);
        return;
      }

      // Basic token validation (check if it's a JWT-like structure)
      const tokenParts = adminToken.split('.');
      if (tokenParts.length !== 3) {
        if (process.env.NODE_ENV === 'development') {
          console.error('   ❌ Invalid adminToken structure (not a JWT)');
        }
        // Clean up invalid token
        localStorage.removeItem('adminToken');
        setIsAuthenticated(false);
        setIsValidating(false);
        return;
      }

      // Optional: Add additional validation like token expiration check
      try {
        // Try to parse the payload part of the JWT
        const payload = JSON.parse(atob(tokenParts[1]));
        if (process.env.NODE_ENV === 'development') {
          console.log('   ✅ Token payload:', payload);
          console.log('   ✅ Token expiration:', new Date(payload.exp * 1000).toLocaleString());
          
          // Check if token is expired
          if (payload.exp && Date.now() >= payload.exp * 1000) {
            console.warn('   ⚠️  Token expired');
            localStorage.removeItem('adminToken');
            setIsAuthenticated(false);
          } else {
            console.log('   ✅ Token is valid');
            setIsAuthenticated(true);
          }
        } else {
          // In production, just set as authenticated if basic checks pass
          setIsAuthenticated(true);
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('   ❌ Failed to parse token payload:', error);
        }
        localStorage.removeItem('adminToken');
        setIsAuthenticated(false);
      }
      
      setIsValidating(false);
    };

    validateAdminToken();
  }, []);

  // Show loading state while validating token
  if (isValidating) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div>Verifying admin authentication...</div>
        <div style={{ fontSize: '0.9rem', color: '#666' }}>
          Checking admin privileges
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated, render children if authenticated
  return isAuthenticated ? children : <Navigate to="/admin/login" replace />;
};

export default ProtectedAdminRoute;